// src/AuthContext.js
import React, { createContext, useState, useEffect, useRef, useCallback } from "react";
import { setAuthToken } from "./api";

export const AuthContext = createContext(null);

const STORAGE_KEY = "hhh_auth";
const INACTIVITY_MS = 15 * 60 * 1000; // 15 minutes
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];

// Decode a JWT's `exp` and check expiry client-side, so a stale token isn't
// restored on load (the backend still validates every token regardless).
function isJwtExpired(token) {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(part));
    if (!payload.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true; // unparseable → treat as invalid
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef(null);

  // Restore session from localStorage on first mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.token && parsed.user && !isJwtExpired(parsed.token)) {
          setUser(parsed.user);
          setTokenState(parsed.token);
          setAuthToken(parsed.token);
        } else {
          localStorage.removeItem(STORAGE_KEY); // drop stale/expired session
        }
      }
    } catch (e) {
      console.error("Failed to parse stored auth", e);
    } finally {
      setAuthReady(true);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setTokenState(null);
    setAuthToken(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Inactivity auto-logout: reset timer on any user activity; check every minute
  useEffect(() => {
    if (!user) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }

    lastActivityRef.current = Date.now();

    const onActivity = () => { lastActivityRef.current = Date.now(); };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    timerRef.current = setInterval(() => {
      if (Date.now() - lastActivityRef.current > INACTIVITY_MS) {
        sessionStorage.setItem("hhh_logout_reason", "inactivity");
        logout();
      }
    }, 60_000);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [user, logout]);

  const login = (userObj, jwtToken) => {
    setUser(userObj);
    setTokenState(jwtToken);
    setAuthToken(jwtToken);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ user: userObj, token: jwtToken }));
  };

  const updateUser = (patch) => {
    setUser((prev) => {
      const next = { ...prev, ...patch };
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...parsed, user: next }));
        } catch {}
      }
      return next;
    });
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser, authReady }}>
      {children}
    </AuthContext.Provider>
  );
}
